import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/chart";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";

type ClientChartRow = {
  month_year: string;
  dateTime: string;
  clients: number;
  activeAudiences?: number;
  highPerformanceAudiences?: number;
  activeOrganizationNames?: string[];
  highPerformanceOrganizationNames?: string[];
};

type ClientChartData = {
  chartData: ClientChartRow[];
  clientNames: { [key: string]: string[] };
  newClientNames?: { [key: string]: string[] };
  showAudienceMetrics?: boolean;
};

const chartConfig = {
  clients: {
    label: "Clients",
    color: "hsl(var(--chart-1))",
  },
  activeAudiences: {
    label: ">200% CPI",
    color: "hsl(var(--chart-1))",
  },
  highPerformanceAudiences: {
    label: "≤200% CPI",
    color: "hsl(var(--chart-2))",
  },
} satisfies ChartConfig;

function GenericClientChart({ chartData, clientNames, newClientNames, showAudienceMetrics }: ClientChartData) {
  const renderClientList = (orgNames: string[], month_year: string) => {
    const newClients: string[] = [];
    const existingClients: string[] = [];

    orgNames.forEach((name: string) => {
      const isNew = newClientNames?.[month_year]?.includes(name);
      if (isNew) {
        newClients.push(name);
      } else {
        existingClients.push(name);
      }
    });

    const sortedOrgNames = [...newClients, ...existingClients];

    return sortedOrgNames.length > 0 ? (
      <ul className="mt-2 w-full justify-start">
        {sortedOrgNames.map((name: string, idx: number) => {
          const isNew = newClientNames?.[month_year]?.includes(name);
          return (
            <li key={idx} className="flex items-center text-left text-xs font-normal text-muted-foreground">
              {name}
              {isNew && (
                <span className="ml-2 rounded bg-[hsl(var(--chart-3))] px-1.5 py-0.2 text-[10px] font-semibold text-white dark:text-black">
                  New
                </span>
              )}
            </li>
          );
        })}
      </ul>
    ) : null;
  };

  const getClientNames = (month_year: string) => {
    const clients = clientNames[month_year] || [];

    if (clients.length === 0) {
      return <ul className="justify-start">No clients</ul>;
    }

    return <ul className="justify-start">{renderClientList(clients, month_year)}</ul>;
  };

  const renderAudienceTooltip = (
    label: string,
    colorVar: string,
    count: number,
    orgNames: string[],
    month_year: string,
  ) => {
    return (
      <>
        <div
          className="h-2.5 w-2.5 shrink-0 rounded-[2px] bg-[--color-bg]"
          style={
            {
              "--color-bg": `var(--color-${colorVar})`,
            } as React.CSSProperties
          }
        />
        {label}
        <div className="ml-auto flex items-baseline gap-0.5 font-mono font-medium tabular-nums text-foreground">
          {count}
        </div>
        {renderClientList(orgNames, month_year)}
      </>
    );
  };

  return (
    <ChartContainer config={chartConfig}>
      <LineChart data={chartData}>
        <CartesianGrid vertical={false} />
        <XAxis dataKey="dateTime" tickLine={false} axisLine={false} tickMargin={8} />
        <YAxis tickLine={false} axisLine={false} tickMargin={8} />
        <ChartTooltip
          cursor={false}
          content={
            <ChartTooltipContent
              className="w-[300px]"
              formatter={(value, name, item, index) => {
                if (showAudienceMetrics) {
                  // For audience metrics mode
                  if (name === "activeAudiences") {
                    const orgNames = item.payload.activeOrganizationNames || [];
                    const highPerfNames = item.payload.highPerformanceOrganizationNames || [];

                    // Filter out high-performance clients from active clients to avoid duplication
                    const activeOnlyNames = orgNames.filter((name: string) => !highPerfNames.includes(name));

                    return renderAudienceTooltip(
                      "> 200% CPI Achievement",
                      "activeAudiences",
                      activeOnlyNames.length,
                      activeOnlyNames,
                      item.payload.month_year,
                    );
                  }
                  if (name === "highPerformanceAudiences") {
                    const orgNames = item.payload.highPerformanceOrganizationNames || [];

                    return renderAudienceTooltip(
                      "≤ 200% CPI Achievement",
                      "highPerformanceAudiences",
                      orgNames.length,
                      orgNames,
                      item.payload.month_year,
                    );
                  }
                  return null;
                } else {
                  // For regular clients mode
                  return (
                    <>
                      <div
                        className="h-2.5 w-2.5 shrink-0 rounded-[2px] bg-[--color-bg]"
                        style={
                          {
                            "--color-bg": `var(--color-${name})`,
                          } as React.CSSProperties
                        }
                      />
                      {chartConfig[name as keyof typeof chartConfig]?.label}

                      <div className="block w-full justify-start items-baseline gap-0.5 font-mono font-medium tabular-nums text-foreground">
                        {getClientNames(item.payload.month_year)}
                      </div>
                      <div className="mt-1.5 flex basis-full items-center border-t pt-1.5 text-xs font-medium text-foreground">
                        Total
                        <div className="ml-auto flex items-baseline gap-0.5 font-mono font-medium tabular-nums text-foreground">
                          {clientNames[item.payload.month_year]?.length || 0} clients
                        </div>
                      </div>
                    </>
                  );
                }
              }}
            />
          }
        />
        {showAudienceMetrics ? (
          <>
            <Line
              dataKey="activeAudiences"
              type="linear"
              stroke="var(--color-activeAudiences)"
              strokeWidth={2}
              dot={false}
            />
            <Line
              dataKey="highPerformanceAudiences"
              type="linear"
              stroke="var(--color-highPerformanceAudiences)"
              strokeWidth={2}
              dot={false}
            />
          </>
        ) : (
          <Line dataKey="clients" type="linear" stroke="var(--color-clients)" strokeWidth={2} dot={false} />
        )}
        <ChartLegend content={<ChartLegendContent />} />
      </LineChart>
    </ChartContainer>
  );
}

export default GenericClientChart;
